<template>
  <!--
    Nachträgliche Mitteilung zu einem Telefonat (Rückmeldung Kanzlei
    01.10.2026: „Wie geht das bei bestehenden Notizen oder wenn es
    automatisch erstellt wird, weil ich übers System rausgerufen habe oder
    angerufen wurde?“). Gilt für vermerkte Telefonate und Anrufe über das
    KARS-Telefon.
  -->
  <v-dialog :model-value="modelValue" max-width="560" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title>{{ title || $gt('Telefonnotiz per E-Mail senden') }}</v-card-title>
      <v-card-text>
        <div v-if="call" class="pnsd-call">
          <div class="pnsd-call__head">
            <PhoneIncoming v-if="call.direction === 'inbound'" :size="15" />
            <PhoneOutgoing v-else :size="15" />
            <strong>{{ call.contactName || call.contactPhone || $gt('Unbekannter Kontakt') }}</strong>
            <span class="pnsd-call__when">{{ formattedDate }}</span>
          </div>
          <div v-if="call.keyword" class="pnsd-call__keyword">{{ call.keyword }}</div>
          <ContactBehaviorChips
            v-if="!withNote"
            class="pnsd-call__behavior"
            :behavior="call.contactBehavior || []"
            :note="call.contactBehaviorNote || null"
          />
          <template v-if="!withNote">
            <p v-if="content" class="pnsd-call__content">{{ content }}</p>
            <p v-else class="pnsd-call__empty">{{ $gt('Kein Gesprächsinhalt hinterlegt.') }}</p>
          </template>
        </div>

        <!-- Abfrage nach einem Anruf über KARS: Notiz direkt erfassen; sie
             wird am Anruf gespeichert und ist Inhalt der Mitteilung. -->
        <template v-if="withNote">
          <v-textarea
            v-model="note"
            :label="$gt('Gesprächsnotiz')"
            :placeholder="$gt('Was wurde besprochen?')"
            rows="3"
            auto-grow
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            autofocus
          />
          <p class="pnsd-hint">
            {{ $gt('Eine KI-Zusammenfassung erscheint – sofern aktiviert – später zusätzlich in der Telefonliste.') }}
          </p>
          <ContactBehaviorField
            v-model="behavior"
            v-model:note="behaviorNote"
            class="pnsd-behavior"
          />
        </template>

        <NoteRecipientsFields
          v-if="modelValue"
          v-model="notify"
          :case-id="caseId"
          :external-suggestions="externalSuggestions"
          @update:loading="loading = $event"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="$emit('update:modelValue', false)">
          {{ withNote ? $gt('Später') : $gt('Abbrechen') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="sending"
          :disabled="!canSend"
          @click="send"
        >{{ sendLabel }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { PhoneIncoming, PhoneOutgoing } from "lucide-vue-next";
import NoteRecipientsFields from "./NoteRecipientsFields.vue";
import ContactBehaviorField from "./ContactBehaviorField.vue";
import ContactBehaviorChips from "./ContactBehaviorChips.vue";
import { buildNotify, invalidExternalEmails } from "@/utils/phoneCallNote";
import { normalizeContactBehavior, normalizeContactBehaviorNote } from "@/utils/contactBehavior";

function emptyNotify() {
  return { userIds: [], emails: [], callbackFollowup: false };
}

export default {
  name: "PhoneNoteSendDialog",
  components: { PhoneIncoming, PhoneOutgoing, NoteRecipientsFields, ContactBehaviorField, ContactBehaviorChips },
  props: {
    modelValue: { type: Boolean, default: false },
    /** Telefonat (manuell oder Portal-Anruf) aus der Telefonliste. */
    call: { type: Object, default: null },
    sending: { type: Boolean, default: false },
    /** Vorschläge für externe Empfänger: [{ name, email, role }] */
    externalSuggestions: { type: Array, default: () => [] },
    /** Abfrage nach dem Anruf: mit Feld für die Gesprächsnotiz. */
    withNote: { type: Boolean, default: false },
    title: { type: String, default: "" },
  },
  emits: ["update:modelValue", "send"],
  data() {
    return { notify: emptyNotify(), loading: false, note: "", behavior: [], behaviorNote: "" };
  },
  computed: {
    caseId() {
      return this.call?.idCase ? Number(this.call.idCase) : null;
    },
    content() {
      return (this.call?.summary || this.call?.aiSummary || "").trim();
    },
    formattedDate() {
      const d = new Date(this.call?.calledAt || "");
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    hasRecipients() {
      return this.notify.userIds.length > 0 || this.notify.emails.length > 0;
    },
    canSend() {
      if (this.loading || invalidExternalEmails(this.notify.emails).length > 0) return false;
      // Mit Notizfeld genügt auch das bloße Speichern der Notiz bzw. des
      // Gesprächsverhaltens.
      return this.hasRecipients || (this.withNote && (this.note.trim() !== "" || this.hasBehavior));
    },
    hasBehavior() {
      return this.behavior.length > 0 || this.behaviorNote.trim() !== "";
    },
    sendLabel() {
      if (!this.withNote) return this.$gt("Senden");
      return this.hasRecipients ? this.$gt("Speichern und senden") : this.$gt("Notiz speichern");
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.notify = emptyNotify();
        this.note = this.call?.summary || "";
        this.behavior = normalizeContactBehavior(this.call?.contactBehavior);
        this.behaviorNote = this.call?.contactBehaviorNote || "";
      }
    },
  },
  methods: {
    /**
     * Meldet Mitteilung (oder undefined) und – mit Notizfeld – die Notiz und
     * das Gesprächsverhalten ({ contactBehavior, contactBehaviorNote }).
     */
    send() {
      const payload = buildNotify(this.notify, !!this.caseId);
      if (!payload && !this.withNote) return;
      if (!this.withNote) {
        this.$emit("send", payload, null, null);
        return;
      }
      this.$emit("send", payload, this.note.trim(), {
        contactBehavior: normalizeContactBehavior(this.behavior),
        contactBehaviorNote: normalizeContactBehaviorNote(this.behaviorNote),
      });
    },
  },
};
</script>

<style scoped>
.pnsd-call {
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 0.86rem;
  color: #0f172a;
}
.pnsd-call__head { display: flex; align-items: center; gap: 8px; }
.pnsd-call__when { margin-left: auto; color: #64748b; font-size: 0.78rem; font-variant-numeric: tabular-nums; }
.pnsd-call__keyword { margin-top: 4px; font-weight: 600; }
.pnsd-call__behavior { margin-top: 6px; }
.pnsd-behavior { margin-top: 12px; }
.pnsd-call__content {
  margin: 6px 0 0;
  white-space: pre-wrap;
  color: #334155;
  max-height: 120px;
  overflow-y: auto;
}
.pnsd-call__empty { margin: 6px 0 0; color: #94a3b8; font-size: 0.8rem; }
.pnsd-hint { margin: 4px 0 6px; font-size: 0.76rem; color: #64748b; }
</style>
